// INSTALL APP (2026-11-02; owner follow-up: "a small install button on the top right of the screen"): one round icon
// button fixed in the title screen's top-right corner (inside the safe area). It shows whenever the game can be installed
// and isn't already running as the installed app:
//  - Chrome / Edge offered beforeinstallprompt -> tapping opens the browser's install dialog;
//  - iPhone / iPad Safari -> a short "Share > Add to Home Screen" hint;
//  - any other secure page (prompt not offered yet, Firefox, ...) -> a short hint with that browser's menu path.
// The title menu's spatial navigation includes it (main.ts TITLE_BTNS): Up from the top menu button reaches it.
import { isStandalone } from './pwa';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// captured at module load (before the title exists): the event can fire while the game is still loading
let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners: (() => void)[] = [];
const changed = () => listeners.forEach((f) => f());
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferred = e as BeforeInstallPromptEvent; changed(); });
  window.addEventListener('appinstalled', () => { deferred = null; installed = true; changed(); });
}

/** iPhone / iPad Safari (incl. iPadOS that reports itself as a Mac): no install prompt API, only Share > Add to Home Screen */
export function isIosSafari(ua = navigator.userAgent, touchPoints = navigator.maxTouchPoints || 0): boolean {
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);
  return ios && /Safari\//.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

/** where the install command lives in this browser's own menu (used when no install prompt was offered) */
export function installHint(ua = navigator.userAgent, touchPoints = navigator.maxTouchPoints || 0): string {
  if (isIosSafari(ua, touchPoints)) return 'To install: tap Share, then "Add to Home Screen".';
  const android = /Android/.test(ua);
  if (/Firefox\//.test(ua)) return android ? 'To install: open the ⋮ menu, then "Install" (or "Add to Home screen").' : 'This browser can\'t install the game as an app. Use Chrome or Edge.';
  if (/SamsungBrowser\//.test(ua)) return 'To install: open the ≡ menu, then "Add page to" › "Home screen".';
  if (/Edg\//.test(ua)) return 'To install: open the ⋯ menu, then "Apps" › "Install this site as an app".';
  if (android) return 'To install: open the ⋮ menu, then "Install app" (or "Add to Home screen" › "Install").';
  return 'To install: open the ⋮ menu, then "Cast, save and share" › "Install page as app", or use the install icon in the address bar.';
}

export interface InstallButton { readonly visible: boolean }

const ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.5v10.5m0 0-4.6-4.6M12 14l4.6-4.6" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M4.5 15.5v2.8a2.2 2.2 0 0 0 2.2 2.2h10.6a2.2 2.2 0 0 0 2.2-2.2v-2.8" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/></svg>';

/** adds the corner button to the title screen; onChange runs when it appears / hides (so the menu focus can be fixed) */
export function initInstallButton(titleSel: string, o: { toast(text: string): void; onChange(): void }): InstallButton {
  const title = document.querySelector(titleSel);
  const btn = document.createElement('button');
  btn.id = 'install-fab'; btn.type = 'button'; btn.hidden = true;
  btn.setAttribute('aria-label', 'Install app'); btn.title = 'Install app';
  btn.innerHTML = ICON;
  const tip = document.createElement('p');
  tip.id = 'install-tip'; tip.setAttribute('role', 'status'); tip.hidden = true;
  title?.append(btn, tip); // after #main-menu: last in the title's navigation order
  let tipTimer = 0;
  const hideTip = () => { tip.hidden = true; clearTimeout(tipTimer); };
  const showTip = (t: string) => { tip.textContent = t; tip.hidden = false; clearTimeout(tipTimer); tipTimer = window.setTimeout(hideTip, 8000); };

  const paint = () => {
    const show = !installed && !isStandalone() && (!!deferred || isIosSafari() || window.isSecureContext);
    if (!show) hideTip();
    if (show === !btn.hidden) return;
    btn.hidden = !show;
    o.onChange();
  };
  btn.addEventListener('click', async (ev) => {
    ev.stopPropagation(); // the title's own click handler would leave the PRESS START splash
    if (!tip.hidden) { hideTip(); return; }
    if (deferred) {
      const d = deferred; deferred = null;
      try { await d.prompt(); const c = await d.userChoice; if (c.outcome === 'accepted') { installed = true; o.toast('INSTALLING…'); } }
      catch { showTip(installHint()); } // prompt already used / blocked: the browser menu still offers Install
      paint();
    } else showTip(installHint());
  });
  tip.addEventListener('click', (ev) => { ev.stopPropagation(); hideTip(); });
  document.addEventListener('pointerdown', (ev) => { if (!tip.hidden && !btn.contains(ev.target as Node) && ev.target !== tip) hideTip(); });
  listeners.push(paint);
  for (const q of ['(display-mode: standalone)', '(display-mode: fullscreen)']) matchMedia(q).addEventListener?.('change', paint);
  paint();
  return { get visible() { return !btn.hidden; } };
}
